import { useState, useRef, useEffect } from 'react';
import { Parameter } from '@shared/types';
import { getParameterIcon } from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// ---------------------------------------------------------------------------
// Colour palette — one distinct colour per option, cycling if needed
// ---------------------------------------------------------------------------

const OPTION_COLOURS = [
  { bg: 'hsl(220 70% 55%)', text: '#fff' }, // blue
  { bg: 'hsl(350 65% 55%)', text: '#fff' }, // red
  { bg: 'hsl(145 55% 45%)', text: '#fff' }, // green
  { bg: 'hsl(35 85% 55%)', text: '#fff' }, // amber
  { bg: 'hsl(280 60% 58%)', text: '#fff' }, // purple
  { bg: 'hsl(185 60% 45%)', text: '#fff' }, // teal
  { bg: 'hsl(20 70% 52%)', text: '#fff' }, // orange
  { bg: 'hsl(60 55% 50%)', text: '#000' }, // yellow
];

// Colour for the "empty" / blank sentinel option — rendered as muted
const EMPTY_COLOUR = { bg: 'hsl(0 0% 20%)', text: 'hsl(0 0% 55%)' };

/** Pick white or black text depending on the perceived brightness of a hex bg. */
function contrastText(hex: string): string {
  const h = hex.replace('#', '');
  // Expand 3-char shorthand to 6-char
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // Perceived luminance (sRGB coefficients)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#000' : '#fff';
}

function optionColour(
  option: string,
  options: string[],
  customColours?: Record<string, string>,
): { bg: string; text: string } {
  // Treat the last option as "empty" when its name contains "empty" / "none" / "—"
  const isBlank = /empty|none|blank|—/i.test(option);
  if (isBlank) return EMPTY_COLOUR;
  // Use custom colour if provided
  const custom = customColours?.[option];
  if (custom) return { bg: custom, text: contrastText(custom) };
  const idx = options.indexOf(option);
  // Guard: if the value isn't in the current options list (e.g. options were
  // just edited in the annotation), fall back to the empty/muted colour.
  if (idx < 0) return EMPTY_COLOUR;
  return OPTION_COLOURS[idx % OPTION_COLOURS.length];
}

// ---------------------------------------------------------------------------
// Label display levels — derived from the measured cell pixel width
// ---------------------------------------------------------------------------

type LabelMode = 'full' | 'short' | 'single' | 'none';

/** Decide how much text to show inside each cell given its pixel size. */
function labelMode(cellPx: number): LabelMode {
  if (cellPx >= 36) return 'full';
  if (cellPx >= 24) return 'short';
  if (cellPx >= 16) return 'single';
  return 'none';
}

/** Return the label text for a cell given the current mode. */
function cellLabel(value: string, mode: LabelMode): string {
  if (mode === 'none') return '';
  if (mode === 'single') return value.charAt(0);
  if (mode === 'short') return value.length > 4 ? value.slice(0, 4) : value;
  // 'full' — still cap at a reasonable length so very long names don't overflow
  return value.length > 8 ? value.slice(0, 8) : value;
}

// ---------------------------------------------------------------------------
// Shallow comparison for string arrays — avoids unnecessary state resets
// when the parent creates a new array reference with identical content.
// ---------------------------------------------------------------------------

function flatArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Span helpers
// ---------------------------------------------------------------------------

/** The sentinel value written into cells covered by a spanning tile. */
const SPAN_SENTINEL = 'span';

/** Return the [colSpan, rowSpan] for an option, defaulting to [1,1]. */
function getSpan(
  option: string,
  optionSpans?: Record<string, [number, number]>,
): [number, number] {
  return optionSpans?.[option] ?? [1, 1];
}

/**
 * Given a cell at (row, col) that contains SPAN_SENTINEL, walk left then up
 * to find the top-left origin cell of the spanning tile.
 * Returns [originRow, originCol], or [row, col] if no origin found (safe fallback).
 */
function findSpanOrigin(
  row: number,
  col: number,
  flat: string[],
  cols: number,
): [number, number] {
  // Walk left along the same row first
  for (let c = col - 1; c >= 0; c--) {
    if (flat[row * cols + c] !== SPAN_SENTINEL) {
      return [row, c];
    }
  }
  // Walk up along the original column
  for (let r = row - 1; r >= 0; r--) {
    if (flat[r * cols + col] !== SPAN_SENTINEL) {
      return [r, col];
    }
  }
  return [row, col];
}

/**
 * Return all [row, col] cells in a W×H footprint starting at (originRow, originCol),
 * clamped to grid bounds.
 */
function footprintCells(
  originRow: number,
  originCol: number,
  colSpan: number,
  rowSpan: number,
  totalCols: number,
  totalRows: number,
): [number, number][] {
  const cells: [number, number][] = [];
  for (let dr = 0; dr < rowSpan; dr++) {
    for (let dc = 0; dc < colSpan; dc++) {
      const r = originRow + dr;
      const c = originCol + dc;
      if (r < totalRows && c < totalCols) {
        cells.push([r, c]);
      }
    }
  }
  return cells;
}

/**
 * Return the set of flat indices occupied by the tile whose top-left is at
 * (originRow, originCol) with the given span.
 */
function footprintSet(
  originRow: number,
  originCol: number,
  colSpan: number,
  rowSpan: number,
  totalCols: number,
  totalRows: number,
): Set<number> {
  const s = new Set<number>();
  for (const [r, c] of footprintCells(
    originRow,
    originCol,
    colSpan,
    rowSpan,
    totalCols,
    totalRows,
  )) {
    s.add(r * totalCols + c);
  }
  return s;
}

/**
 * Determine whether `option` can be placed at (targetRow, targetCol).
 *
 * An option is placeable when:
 * 1. Its full W×H footprint fits within the grid bounds.
 * 2. Every cell in the footprint is either:
 *    - Part of the tile currently occupying (targetRow, targetCol) — being replaced, or
 *    - Exactly `emptyOption` (the first option, conventionally "empty") — a free 1×1 cell.
 */
function canPlaceFull(
  option: string,
  targetRow: number,
  targetCol: number,
  flat: string[],
  totalCols: number,
  totalRows: number,
  emptyOption: string,
  optionSpans?: Record<string, [number, number]>,
): boolean {
  const [w, h] = getSpan(option, optionSpans);
  if (targetCol + w > totalCols || targetRow + h > totalRows) return false;

  const currentVal = flat[targetRow * totalCols + targetCol];
  let ownOriginRow = targetRow;
  let ownOriginCol = targetCol;
  if (currentVal === SPAN_SENTINEL) {
    [ownOriginRow, ownOriginCol] = findSpanOrigin(
      targetRow,
      targetCol,
      flat,
      totalCols,
    );
  }
  const ownTileVal = flat[ownOriginRow * totalCols + ownOriginCol];
  const [ow, oh] = getSpan(ownTileVal, optionSpans);
  const ownCells = footprintSet(
    ownOriginRow,
    ownOriginCol,
    ow,
    oh,
    totalCols,
    totalRows,
  );

  for (const [r, c] of footprintCells(
    targetRow,
    targetCol,
    w,
    h,
    totalCols,
    totalRows,
  )) {
    const idx = r * totalCols + c;
    if (ownCells.has(idx)) continue;
    const cellVal = flat[idx];
    if (cellVal === SPAN_SENTINEL) return false;
    // Must be the empty/first option (a free 1×1 cell)
    if (cellVal !== emptyOption) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GridInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: Parameter['value']) => void;
}) {
  const cfg = param.gridConfig;
  if (!cfg) return null;

  const { options, optionColours: customColours, optionSpans } = cfg;
  // Resolve cols/rows to concrete numbers (may still be a $-ref string if
  // the referenced parameter hasn't been parsed yet — fall back to 1).
  const cols = typeof cfg.cols === 'number' ? cfg.cols : 1;
  const rows = typeof cfg.rows === 'number' ? cfg.rows : 1;
  const totalCells = cols * rows;

  // The first option is treated as the "empty" / free-cell sentinel for placement checks.
  const emptyOption = options[0];

  const flatValue = Array.isArray(param.value)
    ? (param.value as string[])
    : Array(totalCells).fill(emptyOption);

  // Normalise flat array: resize to current cols×rows, preserve existing cell
  // values where possible, and run a sanity pass to fix any tiles that no
  // longer fit within the new bounds.
  //
  // The sanity pass is needed when the grid shrinks: a spanning tile whose
  // origin survives but whose footprint extends into removed columns/rows must
  // be clamped; orphaned "span" sentinels whose origin was removed must be
  // replaced with emptyOption.  We rebuild from scratch using only the
  // top-left cells of each tile so the result is always self-consistent.
  function normalisedFlat(src: string[]): string[] {
    // ── Step 1: first-pass — build a raw grid of the new size ─────────────
    // Copy values from src where cells still exist, fill new cells with
    // emptyOption.  At this point sentinels from the old layout may be
    // stale if the grid shrank.
    const raw: string[] = [];
    for (let i = 0; i < totalCells; i++) {
      const v = src[i];
      if (v === SPAN_SENTINEL) {
        raw.push(SPAN_SENTINEL);
      } else {
        raw.push(options.includes(v) ? v : emptyOption);
      }
    }

    // ── Step 2: rebuild — visit every cell top-left to bottom-right ───────
    // We write into a fresh result array so each cell is resolved exactly once.
    const result: string[] = Array(totalCells).fill(emptyOption);
    // claimed[idx] = true once a cell has been assigned by a tile's footprint
    const claimed = new Uint8Array(totalCells);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const idx = row * cols + col;
        if (claimed[idx]) continue; // already written by a spanning tile above

        const v = raw[idx];

        if (v === SPAN_SENTINEL) {
          // Orphaned sentinel — its origin is no longer in this grid (was in a
          // column/row that was removed).  Replace with emptyOption.
          result[idx] = emptyOption;
          claimed[idx] = 1;
          continue;
        }

        // v is a tile top-left (or emptyOption).
        const [sw, sh] = getSpan(v, optionSpans);

        // Check whether the full span fits within the new bounds.
        const fitsCol = col + sw <= cols;
        const fitsRow = row + sh <= rows;

        if (!fitsCol || !fitsRow) {
          // Tile no longer fits — demote to emptyOption and don't claim
          // any sentinels (they'll be handled as orphans in their own
          // iterations, or were already outside the new size).
          result[idx] = emptyOption;
          claimed[idx] = 1;
          continue;
        }

        // Tile fits — write origin and claim all sentinel cells.
        result[idx] = v;
        claimed[idx] = 1;
        for (const [r, c] of footprintCells(row, col, sw, sh, cols, rows)) {
          if (r === row && c === col) continue;
          const sidx = r * cols + c;
          result[sidx] = SPAN_SENTINEL;
          claimed[sidx] = 1;
        }
      }
    }

    return result;
  }

  // Keep a local copy for optimistic UI updates — no throttle needed since
  // each change is a discrete click, not a continuous drag.
  const [localFlat, setLocalFlat] = useState<string[]>(() =>
    normalisedFlat(flatValue),
  );

  // Sync when parent pushes a genuinely different value OR when dimensions
  // change (because a referenced parameter like $panel_cols changed value).
  const prevDimsRef = useRef<{ cols: number; rows: number }>({ cols, rows });
  const dimsChanged =
    prevDimsRef.current.cols !== cols || prevDimsRef.current.rows !== rows;

  if (dimsChanged) {
    prevDimsRef.current = { cols, rows };
    const src = Array.isArray(param.value) ? (param.value as string[]) : [];
    setLocalFlat(normalisedFlat(src));
  } else {
    const incoming = normalisedFlat(
      Array.isArray(param.value) ? (param.value as string[]) : [],
    );
    if (!flatArraysEqual(localFlat, incoming)) {
      setLocalFlat(incoming);
    }
  }

  // When dimensions change, commit the sanitised layout back to the source so
  // the SCAD code stays in sync immediately (not only on the next cell click).
  // Using a ref to avoid committing on every render — only fire when cols/rows
  // actually changed.
  const commitDimsRef = useRef<{ cols: number; rows: number }>({
    cols: -1,
    rows: -1,
  });
  useEffect(() => {
    if (
      commitDimsRef.current.cols === cols &&
      commitDimsRef.current.rows === rows
    )
      return;
    commitDimsRef.current = { cols, rows };
    // Re-compute the sanitised value synchronously so the effect captures the
    // correct result even if localFlat state hasn't flushed yet.
    const src = Array.isArray(param.value) ? (param.value as string[]) : [];
    const sanitised = normalisedFlat(src);
    onCommit(sanitised);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols, rows]);

  // Track which cell's popover is open (null = none)
  const [openCell, setOpenCell] = useState<number | null>(null);

  const cellIndex = (row: number, col: number) => row * cols + col;

  /**
   * Commit a new option choice for the cell at (row, col).
   *
   * Algorithm:
   * 1. Resolve the current tile's origin and clear its entire footprint to emptyOption.
   * 2. Write the new option at (row, col).
   * 3. Write SPAN_SENTINEL into every other cell of the new option's footprint
   *    (clamped to grid bounds).
   */
  const handleSelect = (row: number, col: number, option: string) => {
    const next = [...localFlat];

    // ── 1. Clear the existing tile at this cell ──────────────────────────────
    const currentVal = next[row * cols + col];
    let ownOriginRow = row;
    let ownOriginCol = col;
    if (currentVal === SPAN_SENTINEL) {
      [ownOriginRow, ownOriginCol] = findSpanOrigin(row, col, next, cols);
    }
    const ownTileVal = next[ownOriginRow * cols + ownOriginCol];
    const [ow, oh] = getSpan(ownTileVal, optionSpans);
    for (const [r, c] of footprintCells(
      ownOriginRow,
      ownOriginCol,
      ow,
      oh,
      cols,
      rows,
    )) {
      next[r * cols + c] = emptyOption;
    }

    // ── 2. Write new option at (row, col) ────────────────────────────────────
    next[row * cols + col] = option;

    // ── 3. Write sentinels for the new option's span ─────────────────────────
    const [nw, nh] = getSpan(option, optionSpans);
    for (const [r, c] of footprintCells(row, col, nw, nh, cols, rows)) {
      if (r === row && c === col) continue; // skip the top-left itself
      next[r * cols + c] = SPAN_SENTINEL;
    }

    setLocalFlat(next);
    setOpenCell(null);
    onCommit(next);
  };

  // ---------------------------------------------------------------------------
  // Responsive cell sizing via ResizeObserver
  // ---------------------------------------------------------------------------

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setContainerWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  // Cell size: fill the container exactly, with a minimum of 12 px and
  // a maximum of 56 px so cells don't balloon on very wide panels.
  const cellPx =
    containerWidth > 0
      ? Math.max(12, Math.min(56, Math.floor(containerWidth / cols)))
      : Math.max(12, Math.min(44, Math.floor(220 / Math.max(cols, rows))));

  const mode = labelMode(cellPx);
  const fontSize = Math.max(7, Math.min(11, cellPx / 4));

  const IconComponent = getParameterIcon(param.name);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
      <ParameterLabel
        param={param}
        IconComponent={IconComponent}
        className="pt-1"
      />

      <div className="flex flex-col gap-1.5">
        {/* Measure the available width via this wrapper div */}
        <div ref={containerRef} className="w-full">
          {/* Grid — fills the full measured width */}
          <div
            className="grid gap-px overflow-hidden rounded-md border border-border"
            style={{
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridTemplateRows: `repeat(${rows}, ${cellPx}px)`,
              width: '100%',
            }}
          >
            {Array.from({ length: rows }, (_, row) =>
              Array.from({ length: cols }, (_, col) => {
                const idx = cellIndex(row, col);
                const currentVal = localFlat[idx] ?? emptyOption;

                // Covered cells: rendered by the spanning tile above/left — skip.
                if (currentVal === SPAN_SENTINEL) return null;

                const [colSpan, rowSpan] = getSpan(currentVal, optionSpans);
                // Clamp visual span to grid bounds
                const effectiveColSpan = Math.min(colSpan, cols - col);
                const effectiveRowSpan = Math.min(rowSpan, rows - row);

                const colour = optionColour(currentVal, options, customColours);
                const label = cellLabel(currentVal, mode);

                // Filter available options to those that can actually be placed here.
                const placeableOptions = options.filter((opt) =>
                  canPlaceFull(
                    opt,
                    row,
                    col,
                    localFlat,
                    cols,
                    rows,
                    emptyOption,
                    optionSpans,
                  ),
                );

                const cellStyle: React.CSSProperties = {
                  gridColumn:
                    effectiveColSpan > 1
                      ? `span ${effectiveColSpan}`
                      : undefined,
                  gridRow:
                    effectiveRowSpan > 1
                      ? `span ${effectiveRowSpan}`
                      : undefined,
                  height: effectiveRowSpan > 1 ? undefined : cellPx,
                  backgroundColor: colour.bg,
                  color: colour.text,
                  fontSize,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  letterSpacing: '-0.03em',
                  overflow: 'hidden',
                };

                // With only 2 placeable options, click toggles directly — no popover.
                if (placeableOptions.length <= 2) {
                  const currentIdx = placeableOptions.indexOf(currentVal);
                  const nextVal =
                    placeableOptions.length === 0
                      ? currentVal
                      : placeableOptions[
                          (currentIdx + 1) % placeableOptions.length
                        ];
                  return (
                    <button
                      key={idx}
                      className="flex items-center justify-center transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring active:opacity-60"
                      style={cellStyle}
                      aria-label={`Cell ${col},${row}: ${currentVal}`}
                      onClick={() => handleSelect(row, col, nextVal)}
                    >
                      {label}
                    </button>
                  );
                }

                return (
                  <Popover
                    key={idx}
                    open={openCell === idx}
                    onOpenChange={(open) => setOpenCell(open ? idx : null)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        className="flex items-center justify-center transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring active:opacity-60"
                        style={cellStyle}
                        aria-label={`Cell ${col},${row}: ${currentVal}`}
                      >
                        {label}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-1.5"
                      side="bottom"
                      align="start"
                      sideOffset={4}
                    >
                      <div className="flex flex-col gap-0.5">
                        {placeableOptions.map((opt) => {
                          const c = optionColour(opt, options, customColours);
                          const isActive = opt === currentVal;
                          const [sw, sh] = getSpan(opt, optionSpans);
                          const hasSpan = sw > 1 || sh > 1;
                          return (
                            <button
                              key={opt}
                              className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted/60"
                              style={{ fontWeight: isActive ? 700 : 400 }}
                              onClick={() => handleSelect(row, col, opt)}
                            >
                              <span
                                className="inline-block h-3 w-3 flex-shrink-0 rounded-sm"
                                style={{ backgroundColor: c.bg }}
                              />
                              {opt}
                              {hasSpan && (
                                <span className="ml-1 text-[9px] text-muted-foreground opacity-70">
                                  {sw}×{sh}
                                </span>
                              )}
                              {isActive && (
                                <span className="ml-auto text-[10px] text-muted-foreground">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              }),
            )}
          </div>
        </div>

        {/* Legend — mirrors the label mode: hide text when cells are too small */}
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {options.map((opt) => {
            const c = optionColour(opt, options, customColours);
            const [sw, sh] = getSpan(opt, optionSpans);
            const hasSpan = sw > 1 || sh > 1;
            return (
              <span
                key={opt}
                className="flex items-center gap-1 text-[10px] text-muted-foreground"
              >
                <span
                  className="inline-block h-2 w-2 flex-shrink-0 rounded-sm"
                  style={{ backgroundColor: c.bg }}
                />
                {mode !== 'none' && opt}
                {mode !== 'none' && hasSpan && (
                  <span className="opacity-50">
                    {sw}×{sh}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
